import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FilePdf } from "@phosphor-icons/react";
import jsPDF from "jspdf";

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function buildPdf(r, name) {
  const doc = new jsPDF();
  let y = 16;
  const line = (t, x = 14, bold = false) => { doc.setFont(undefined, bold ? "bold" : "normal"); doc.text(t, x, y); };
  const next = (d = 7) => { y += d; if (y > 280) { doc.addPage(); y = 16; } };
  doc.setFontSize(15); line(`Relatório para Imposto de Renda — ${r.year}`, 14, true); next(7);
  doc.setFontSize(10); line(`${name} · Acerto Rápido JVO · gerado em ${new Date().toLocaleDateString("pt-BR")}`); next(10);
  doc.setFontSize(11); line("Resumo do ano", 14, true); next();
  [["Comissões + bonificações", r.totals.auto_earnings], ["Outras rendas", r.totals.other_income], ["Total de rendas", r.totals.income_total],
   ["Despesas pessoais", r.totals.expenses], ["Despesas dedutíveis no IR", r.totals.deductible], ["Saldo do ano", r.totals.balance]]
    .forEach(([l, v]) => { line(l); line(fmt(v), 150); next(); });
  next(3); line("Despesas por categoria", 14, true); next();
  r.categories.forEach((c) => { line(`${c.category}${c.deductible ? " (dedutível)" : ""} — ${c.count} lanç.`); line(fmt(c.total), 150); next(); });
  if (r.incomes.length) { next(3); line("Outras rendas", 14, true); next(); r.incomes.forEach((i) => { line(i.label); line(fmt(i.total), 150); next(); }); }
  next(3); line("Mês a mês", 14, true); next();
  ["Mês", "Comissões", "Outras rendas", "Despesas", "Saldo"].forEach((h, i) => line(h, [14, 40, 80, 120, 160][i], true)); next();
  r.monthly.forEach((m) => { [MONTHS[m.month - 1], fmt(m.auto_earnings), fmt(m.other_income), fmt(m.expenses), fmt(m.balance)].forEach((v, i) => line(v, [14, 40, 80, 120, 160][i])); next(6); });
  next(4); doc.setFontSize(8); line("Documento auxiliar de organização financeira. Confira os valores com seu contador antes de declarar.");
  doc.save(`relatorio-ir-${r.year}.pdf`);
}

export function IRReport({ year }) {
  const { user } = useAuth();
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/personal/ir-report", { params: { year } }).then((x) => setR(x.data)); }, [year]);
  if (!r) return <p className="text-sm text-slate-500">Carregando…</p>;
  const t = r.totals;
  return (
    <div className="space-y-4" data-testid="ir-report">
      <div className="flex justify-end">
        <Button onClick={() => buildPdf(r, user?.name || "")} data-testid="btn-ir-pdf" className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5"><FilePdf size={16} /> Baixar PDF do IR</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Comissões + bonificações", t.auto_earnings, "text-emerald-700", r.auto_label], ["Outras rendas", t.other_income, "text-emerald-700"], ["Despesas pessoais", t.expenses, "text-rose-700"], ["Dedutível no IR", t.deductible, "text-blue-700"]].map(([l, v, c, sub]) => (
          <Card key={l}><CardContent className="pt-5"><div className="text-xs text-slate-500">{l}</div><div className={`text-xl font-extrabold ${c}`} data-testid={`ir-total-${l}`}>{fmt(v)}</div>{sub && <div className="text-[11px] text-slate-400">{sub}</div>}</CardContent></Card>
        ))}
      </div>
      <Card><CardContent className="pt-5 flex justify-between items-center"><span className="font-semibold">Saldo do ano (rendas − despesas)</span><span className={`text-2xl font-black ${t.balance >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="ir-balance">{fmt(t.balance)}</span></CardContent></Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Despesas por categoria</CardTitle></CardHeader>
        <CardContent className="space-y-1.5">
          {r.categories.length === 0 && <p className="text-sm text-slate-500">Nenhuma despesa lançada.</p>}
          {r.categories.map((c) => (
            <div key={c.category} className="flex justify-between items-center text-sm border-b pb-1.5">
              <span>{c.category} {c.deductible && <Badge className="ml-1 bg-blue-100 text-blue-800 hover:bg-blue-100">dedutível IR</Badge>}</span>
              <span className="font-semibold">{fmt(c.total)}</span>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Mês a mês</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 border-b"><tr><th className="text-left py-1.5">Mês</th><th className="text-right">Comissões</th><th className="text-right">Outras rendas</th><th className="text-right">Despesas</th><th className="text-right">Saldo</th></tr></thead>
            <tbody>
              {r.monthly.map((m) => (
                <tr key={m.month} className="border-b border-slate-100">
                  <td className="py-1.5">{MONTHS[m.month - 1]}</td><td className="text-right">{fmt(m.auto_earnings)}</td><td className="text-right">{fmt(m.other_income)}</td>
                  <td className="text-right text-rose-700">{fmt(m.expenses)}</td><td className={`text-right font-semibold ${m.balance >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmt(m.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <p className="text-xs text-slate-500">Documento auxiliar para organizar sua declaração. Confira os valores com seu contador.</p>
    </div>
  );
}
